import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { ReactNode, useEffect } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRoles?: string[]
}

// Admin portal requires Admin role for all routes
const ADMIN_PORTAL_REQUIRED_ROLE = 'Admin'
const MAIN_SITE_URL = 'https://hadv.quest'

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore()
  const location = useLocation()

  // Determine the set of roles that grant access to this route.
  // Default: Admin-only. The portal is for Admins; Moderators work in the Website and the
  // app. `requiredRoles` overrides the gate for a route that passes it (none do today).
  const allowedRoles =
    requiredRoles && requiredRoles.length > 0
      ? requiredRoles
      : [ADMIN_PORTAL_REQUIRED_ROLE]

  const hasAccess = !!allowedRoles.some(role => user?.roles?.includes(role))

  // Redirect users who lack access to the main site
  useEffect(() => {
    if (isAuthenticated && !hasAccess) {
      console.warn('[ProtectedRoute] Access denied - missing required role, redirecting to main site')
      window.location.href = MAIN_SITE_URL
    }
  }, [isAuthenticated, hasAccess])

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!hasAccess) {
    // Return null while redirecting
    return null
  }

  return <>{children}</>
}
